import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, CalendarRange, List as ListIcon, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import EditReservationDrawer from "@/components/reservations/EditReservationDrawer";
import ReservationFormOverlay from "@/components/reservations/ReservationFormOverlay";
import AgendaDateNav from "@/components/agenda/AgendaDateNav";
import AgendaRow from "@/components/agenda/AgendaRow";
import AgendaWeek, { weekStart, dayStats } from "@/components/agenda/AgendaWeek";
import { STATUS_LABELS, romeToday, shiftDay } from "@/components/agenda/agendaConfig";

const weekRange = (iso) => {
  const from = weekStart(iso);
  return `date_from=${from}&date_to=${shiftDay(from, 6)}`;
};

function useAgendaPerms() {
  const { user } = useAuth();
  return useMemo(() => ({
    canCreate: hasPermission(user, "booking.create"),
    canEdit: hasPermission(user, "booking.edit"),
    canCancel: hasPermission(user, "booking.cancel"),
    canDelete: ["admin", "super_admin"].includes(user?.role),
  }), [user]);
}

function Summary({ rows }) {
  const { count, pax } = dayStats(rows);
  const tile = "rounded-2xl bg-white border border-sand-200 px-4 py-3";
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="agenda-summary">
      <div className={tile}>
        <div className="text-[11px] uppercase tracking-[0.16em] text-ink-500">Prenotazioni</div>
        <div className="font-data text-[30px] font-semibold leading-tight text-ink-900 mt-1" data-testid="agenda-count">{count}</div>
      </div>
      <div className={tile}>
        <div className="text-[11px] uppercase tracking-[0.16em] text-ink-500">Coperti</div>
        <div className="font-data text-[30px] font-semibold leading-tight text-ink-900 mt-1" data-testid="agenda-pax-total">{pax}</div>
      </div>
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  const opts = [{ k: "day", label: "Giorno", Icon: ListIcon }, { k: "week", label: "Settimana", Icon: CalendarRange }];
  return (
    <div className="inline-flex rounded-full border border-sand-300 bg-white p-1" data-testid="agenda-view-toggle">
      {opts.map(({ k, label, Icon }) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`agenda-view-${k}`}
                className={`h-11 px-4 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  view === k ? "bg-ink-900 text-white" : "text-ink-600 hover:bg-sand-100"}`}>
          <Icon className="w-3.5 h-3.5" /> {label}
        </button>
      ))}
    </div>
  );
}

function useReservationActions(load, setDetailId, setDup) {
  return useMemo(() => ({
    open: (r) => setDetailId(r.id),
    duplicate: (r) => setDup({
      date: r.date, time: r.time, pax: r.pax, customer_name: r.customer_name,
      customer_surname: r.customer_surname, customer_phone: r.customer_phone,
      notes: r.notes || "", booking_source: r.booking_source,
    }),
    status: async (r, to, opts = {}) => {
      if (opts.confirm && !window.confirm(`Ripristinare la prenotazione di ${r.customer_name} come "${STATUS_LABELS[to]}"?`)) { return; }
      try {
        await api.patch(`/admin/reservations/${r.id}/status`, { status: to, force: !!opts.force });
        toast.success(`${r.customer_name}: ${STATUS_LABELS[to]}`);
        load();
      } catch (e) { toast.error(formatApiError(e)); }
    },
    resend: async (r) => {
      try {
        const rr = await api.post(`/admin/reservations/${r.id}/resend`, { client_request_id: `ui-${r.id}-${Date.now()}` });
        const n = rr.data?.notification;
        if (n?.status === "accepted") { toast.success("Richiesta accettata da Meta: esito in arrivo"); }
        else { toast.error(n?.error || "Invio non riuscito"); }
      }
      catch (e) { toast.error(formatApiError(e)); }
    },
    copyLink: async (r) => {
      try {
        const res = await api.get(`/admin/reservations/${r.id}`);
        if (!res.data?.confirm_url) { toast.error("Link non disponibile"); return; }
        try { await navigator.clipboard.writeText(res.data.confirm_url); toast.success("Link copiato"); }
        catch { window.prompt("Copia il link:", res.data.confirm_url); }
      } catch (e) { toast.error(formatApiError(e)); }
    },
    remove: async (r) => {
      if (!window.confirm(`Eliminare definitivamente la prenotazione ${r.code}?`)) { return; }
      try { await api.delete(`/admin/reservations/${r.id}`); toast.success("Prenotazione eliminata"); load(); }
      catch (e) { toast.error(formatApiError(e)); }
    },
  }), [load, setDetailId, setDup]);
}

export default function StaffAgenda() {
  const perms = useAgendaPerms();
  const [date, setDate] = useState(romeToday);
  const [view, setView] = useState("day");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [cfg, setCfg] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [dup, setDup] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dayFilter, setDayFilter] = useState(null);
  const changeView = (v) => {
    if (v === "day" && dayFilter) { setDate(dayFilter); }
    setDayFilter(null);
    setView(v);
  };

  const reqRef = useRef(0);
  const load = useCallback(async (silent = false) => {
    const id = ++reqRef.current;
    setLoading(true);
    try {
      const params = view === "day" ? `scope=date&date=${date}` : weekRange(date);
      const r = await api.get(`/admin/reservations?${params}`);
      if (id === reqRef.current) { setRows(r.data || []); }
    } catch (e) { if (!silent && id === reqRef.current) { toast.error(formatApiError(e)); } }
    finally { if (id === reqRef.current) { setLoading(false); } }
  }, [date, view]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(true), 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/admin/reservations/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const on = useReservationActions(load, setDetailId, setDup);
  const closeForm = useCallback(() => { setCreating(false); setDup(null); }, []);
  const rowPerms = useMemo(() => ({ ...perms, waAvailable: Boolean(cfg?.whatsapp_confirm?.available) }), [perms, cfg]);
  const dayRows = useMemo(() => rows.filter((r) => r.date === date)
    .sort((a, b) => (a.time || "").localeCompare(b.time || "")), [rows, date]);
  const matches = useCallback((r) => {
    const s = q.trim().toLowerCase();
    return !s || `${r.customer_name} ${r.customer_surname || ""} ${r.customer_phone || ""} ${r.code}`
      .toLowerCase().includes(s);
  }, [q]);
  const visible = useMemo(() => dayRows.filter(matches), [dayRows, matches]);
  const weekRows = useMemo(() => rows.filter(matches), [rows, matches]);

  return (
    <div className="max-w-3xl lg:max-w-5xl mx-auto px-3 sm:px-5 pt-4 pb-24 space-y-4" data-testid="staff-agenda-page">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-bronze-700">Servizio</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink-900 leading-tight">Agenda Prenotazioni</h1>
        </div>
        {perms.canCreate && (
          <button type="button" onClick={() => setCreating(true)} data-testid="agenda-new-btn"
                  className="h-11 px-4 sm:px-5 rounded-full bg-bronze-600 text-white text-sm font-medium inline-flex items-center gap-1.5
                             shadow-sm hover:bg-bronze-700 active:scale-[0.98] transition-[background-color,transform]">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nuova prenotazione</span><span className="sm:hidden">Nuova</span>
          </button>
        )}
      </header>

      {view === "day" && <AgendaDateNav date={date} onChange={setDate} />}

      <div className="flex items-center justify-between gap-3">
        <ViewToggle view={view} onChange={changeView} />
        <label className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca"
                 data-testid="agenda-search"
                 className="w-full h-11 pl-9 pr-3 rounded-full border border-sand-300 bg-white text-sm focus:outline-none focus:border-bronze-500" />
        </label>
      </div>

      {view === "day" ? (
        <>
          <Summary rows={dayRows} />
          {loading && dayRows.length === 0 ? (
            <div className="py-16 text-center text-sm text-ink-500" data-testid="agenda-loading">Caricamento…</div>
          ) : visible.length === 0 ? (
            <div className="py-16 text-center rounded-2xl border border-dashed border-sand-300 bg-white/60" data-testid="agenda-empty">
              <CalendarDays className="w-6 h-6 mx-auto text-ink-400" />
              <p className="mt-2 text-sm text-ink-500">Nessuna prenotazione per questa data.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="agenda-list">
              {visible.map((r, i) => <AgendaRow key={r.id} r={r} perms={rowPerms} on={on} index={i} />)}
            </ul>
          )}
        </>
      ) : (
        <AgendaWeek date={date} rows={weekRows} dayFilter={dayFilter} onDayFilter={setDayFilter}
                    onDateChange={setDate} perms={rowPerms} on={on} />
      )}

      {detailId && <EditReservationDrawer id={detailId} cfg={cfg} onClose={() => setDetailId(null)} onChanged={load} />}
      {(creating || dup) && (
        <ReservationFormOverlay entryPoint="staff_agenda" prefill={dup || { date }} backLabel="Agenda"
                                onClose={closeForm} onSaved={(res) => { closeForm(); if (res?.date) { setDate(res.date); } load(); }} />
      )}
    </div>
  );
}
